import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { QualityZoneCode, QualityZoneItem, type IndicatorSelectHandler } from '@/entities/dashboards';
import { cn, preventShiftSelection, zoneCardClass } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { StatBox } from './parts/StatBox';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS, fmt } from './utils';

type Props = {
  zones: QualityZoneItem[];
  /** Переход к диалогам оценок зоны */
  onSelectZone: IndicatorSelectHandler;
};

/** Фиксированный порядок блоков виджета: зелёная → жёлтая → красная зона. */
const ZONE_ORDER: QualityZoneCode[] = ['green', 'yellow', 'red'];

/**
 * Карточка «Зоны качества»: три цветных блока с долей и числом оценок в
 * зелёной / жёлтой / красной зоне общего балла. Доли приходят готовыми
 * с бэкенда — клиент только отображает их.
 *
 * Клик по зоне открывает диалоги её оценок (как переход с авто-инсайта). Зона
 * без диалогов разбирать нечего, поэтому переход по ней не предлагается.
 */
export const QualityZonesCard: FC<Props> = ({ zones, onSelectZone }) => {
  const { t } = useTranslation('qualityDashboard');

  // Литеральные ключи перевода — для типобезопасного доступа по коду зоны.
  const zoneLabel: Record<QualityZoneCode, string> = {
    green: t('zones.green'),
    yellow: t('zones.yellow'),
    red: t('zones.red'),
  };

  const byZone = new Map(zones.map((z) => [z.zone, z]));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('zones.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {zones.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ZONE_ORDER.map((zone) => {
              const item = byZone.get(zone);
              const isClickable = !!item && item.conversationIds.length > 0;
              const statBox = (
                <StatBox
                  label={zoneLabel[zone]}
                  value={
                    <>
                      {fmt(item?.pct ?? 0, 1)}%
                      <span className="block text-sm font-normal">
                        {t('zones.evaluations', { count: item?.count ?? 0 })}
                      </span>
                    </>
                  }
                  className={cn(
                    'rounded-xl h-full',
                    zoneCardClass[zone],
                    isClickable && DASHBOARD_CLICKABLE_BLOCK_CLASS,
                  )}
                  valueClassName="text-3xl"
                />
              );

              if (!isClickable || !item) {
                return <div key={zone}>{statBox}</div>;
              }

              return (
                <div
                  key={zone}
                  role="button"
                  tabIndex={-1}
                  aria-label={t('zones.openDialogs')}
                  onMouseDown={preventShiftSelection}
                  onClick={(event) =>
                    onSelectZone({ label: zoneLabel[zone], conversationIds: item.conversationIds }, event.shiftKey)
                  }
                >
                  {statBox}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
